import {useSelector} from 'react-redux';
import {Box, Toolbar} from '@material-ui/core';
import {FormattedMessage, useIntl} from 'react-intl';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {Navigation} from '../Navigation';
import {useAddRawData} from './useAddRawData';
import {LOCAL_TYPE_TO_BINARY} from '../helpers';
import {DataReview} from '../DataReview/DataReview';
import {runJob} from '../../../store/dataService/job';
import {RootState, useAppDispatch} from '../../../store';
import {UploadBinariesTable} from './UploadBinariesTable';
import {toast} from '../../../base-components/StudioToast';
import {UploadBinariesDialog} from './UploadBinariesDialog';
import {BinariesUploadResponse} from '../../../types/dataService/Binaries';
import {ItemType} from '../../../base-components/StudioDataset/FileViewer';
import {DatasetMetadataBinaryItem} from '../../../types/dataService/DatasetMetadata';
import {StudioDatasetView} from '../../../base-components/StudioDataset/StudioDatasetViewer';
import {useStudioDatasetViewerContext} from '../../../base-components/StudioDataset/StudioDatasetViewerProvider';
import {
  DataPreparationOrigin,
  DataPreparationContainer,
} from '../../../types/dataset/DataPreparationContainer';
import {DataPrepareStep, dataPrepareActions} from '../../../store/dataPrepare';

import './DataView.scss';

type DataViewProps = {
  inProgress: boolean;
  dataPreparationContainer: DataPreparationContainer | null;

  onBack: () => void;
  onForward: (settings?: {skipSteps: DataPrepareStep[]}) => void;
};

const {setStatus, setUploadProgress, setOpenUploadDialog} = dataPrepareActions;

const DataViewBase = ({
  inProgress,
  dataPreparationContainer,
  onForward,
  onBack,
}: DataViewProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [uploadFiles, setUploadFiles] = useState<ItemType[]>([]);
  const previousImagesRef = useRef<DatasetMetadataBinaryItem[]>([]);
  const [hasUploadANewImage, setHasUploadedANewImage] = useState(false);

  const {allBinaries, refreshBinaries} = useStudioDatasetViewerContext();

  const hasImagesInDataPreparationContainer = useSelector((state: RootState) =>
    Boolean(state.dataPrepare.annotationData?.images?.length)
  );

  /**
   * Whether we are going back in the creation or coming from an edit,
   * when we have a dataPreparationContainer, we are already working on
   * an existing dataset, so at this step, as long as we have a
   * dataPreparationContainer, we will always be in edit mode.
   */
  const isEditing = useMemo(() => Boolean(dataPreparationContainer?.id), [
    dataPreparationContainer,
  ]);
  const origin: DataPreparationOrigin = useMemo(
    () => dataPreparationContainer?.origin || 'raw',
    [dataPreparationContainer]
  );

  const step = useSelector((state: RootState) => state.dataPrepare.step);
  const dataset = useSelector((state: RootState) => state.dataset.data);
  const openUploadDialog = useSelector(
    (state: RootState) => state.dataPrepare.openUploadDialog
  );

  const [initialized, setInitialized] = useState(false);
  useEffect(
    function initializeLoadingBinariesIfDatasetExist() {
      if (!initialized && dataset) {
        refreshBinaries();
        setInitialized(true);
      }
    },
    [dataset, initialized, refreshBinaries]
  );

  const onUploadDone = useCallback(
    (binaries: BinariesUploadResponse) => {
      if (dataset) {
        dispatch(
          runJob({
            datasetId: dataset.id,
            type: 'ANALYZE_WIP',
          })
        );
      }

      if (binaries && step === DataPrepareStep.DATA_UPLOAD) {
        onForward();
      } else if (step === DataPrepareStep.DATA_REVIEW) {
        refreshBinaries();
      }

      dispatch(setOpenUploadDialog(false));
    },
    [dataset, dispatch, onForward, refreshBinaries, step]
  );

  const initiateAddRawData = useAddRawData({
    datasetId: dataset?.id,
    onResume: () => dispatch(setStatus('UPLOADING')),
    onStart: () => dispatch(setStatus('UPLOADING')),
    onUploadProgress: event => dispatch(setUploadProgress(event)),
    onFinish: response => {
      dispatch(setStatus('IDLE'));
      onUploadDone(response);
      setUploadFiles([]);
    },
    onError: () => {
      dispatch(setStatus('IDLE'));
      toast.error(
        intl.formatMessage({id: 'datasetCreation.firstStep.errorUploadingFiles'})
      );
    },
  });

  const onInitializeUpload = useCallback(() => {
    initiateAddRawData(uploadFiles);
  }, [initiateAddRawData, uploadFiles]);

  const onCloseUploadDialog = useCallback(() => {
    setUploadFiles([]);
    dispatch(setOpenUploadDialog(false));
  }, [dispatch]);

  useEffect(
    function detectWhenNewImagesWereUploaded() {
      const currentImages =
        allBinaries?.group.find(group => group.id === LOCAL_TYPE_TO_BINARY.image)?.list ||
        [];

      const previousImages = previousImagesRef.current;

      const newImages = currentImages.filter(
        currentImage =>
          !previousImages.some(prevImage => prevImage.id === currentImage.id)
      );

      if (newImages.length > 0) {
        setHasUploadedANewImage(true);
      }

      previousImagesRef.current = currentImages;
    },
    [allBinaries]
  );

  const datasetHasFiles = useMemo(() => Boolean(allBinaries?.list.length), [allBinaries]);

  const datasetHasImages = useMemo(
    () =>
      Boolean(
        allBinaries?.group.find(group => group.id === LOCAL_TYPE_TO_BINARY.image)?.list
          .length
      ) || hasImagesInDataPreparationContainer,
    [allBinaries, hasImagesInDataPreparationContainer]
  );

  const hasNewFilesToUpload = useMemo(() => Boolean(uploadFiles.length), [uploadFiles]);

  const isReviewStepWithoutImageUploaded = useMemo(
    () => step === DataPrepareStep.DATA_REVIEW && !datasetHasImages,
    [step, datasetHasImages]
  );

  const isUploadStepWithoutFilesToUpload = useMemo(
    () =>
      step === DataPrepareStep.DATA_UPLOAD && !datasetHasFiles && !hasNewFilesToUpload,
    [step, hasNewFilesToUpload, datasetHasFiles]
  );

  const onLocalForward = useCallback(() => {
    if (!isEditing && step === DataPrepareStep.DATA_UPLOAD && hasNewFilesToUpload) {
      onInitializeUpload();
    } else {
      const skipSteps = [];

      if (!hasUploadANewImage && step === DataPrepareStep.DATA_REVIEW) {
        skipSteps.push(DataPrepareStep.DATA_REVIEW_FINALIZATION);
      }

      onForward({skipSteps});
    }
  }, [
    step,
    isEditing,
    hasUploadANewImage,
    hasNewFilesToUpload,
    onForward,
    onInitializeUpload,
  ]);

  return (
    <>
      <Toolbar className="data-prepare__header" variant="dense">
        <Box flexGrow={1}></Box>
        <Navigation
          onBack={onBack}
          onForward={onLocalForward}
          disableBack={inProgress}
          disableForward={
            inProgress ||
            (!isEditing &&
              (isReviewStepWithoutImageUploaded || isUploadStepWithoutFilesToUpload))
          }
        />
      </Toolbar>

      {!isEditing && step === DataPrepareStep.DATA_UPLOAD ? (
        <div className="data-upload">
          <h2 className="data-upload__header">
            <FormattedMessage id="datasetCreation.firstStep.initialStepTitle" />
          </h2>
          <p className="data-upload__caption">
            <FormattedMessage id="datasetCreation.firstStep.subTitle" />
          </p>
          <p className="data-upload__step-title">
            <FormattedMessage id="datasetCreation.firstStep.step" />
          </p>

          <UploadBinariesTable files={uploadFiles} setFiles={setUploadFiles} />
        </div>
      ) : (
        step === DataPrepareStep.DATA_REVIEW && (
          <>
            <DataReview isEditing={isEditing} origin={origin} />
            <UploadBinariesDialog
              open={openUploadDialog}
              uploadFiles={uploadFiles}
              onClose={onCloseUploadDialog}
              onSubmit={onInitializeUpload}
              onChangeUploadingFilesList={setUploadFiles}
            />
          </>
        )
      )}
    </>
  );
};

export const DataView = (props: DataViewProps) => (
  <StudioDatasetView>
    <DataViewBase {...props} />
  </StudioDatasetView>
);
